<template>
  <div class="margin-card">
    <div class="margin-card__top-line" :style="{ background: semanticColor }"></div>

    <div class="margin-card__header">
      <span class="margin-card__label">Margen operativo</span>
      <span class="margin-card__sublabel">NOI / Ingreso facturado</span>
    </div>

    <template v-if="!comparisonMode">
      <button class="margin-card__body" @click="expanded = !expanded">
        <span class="margin-card__value" :style="{ color: semanticColor }">{{ formatPercent(period?.noiMarginPercent) }}</span>
        <div class="margin-card__sub-col">
          <span class="margin-card__sub margin-card__sub--neutral">Mercado industrial MX: 70-85%</span>
          <span class="margin-card__sub" :class="trendPoints >= 0 ? 'margin-card__sub--good' : 'margin-card__sub--bad'">
            {{ trendPoints >= 0 ? '▲' : '▼' }} {{ Math.abs(trendPoints).toFixed(1) }} pts vs {{ previousPeriodLabel }}
          </span>
          <span class="margin-card__sub margin-card__sub--neutral">Promedio últimos 12 meses: {{ formatPercent(twelveMonthAvgMargin) }}</span>
        </div>
      </button>

      <div v-if="expanded" class="margin-card__expand">
        <div class="margin-card__expand-row margin-card__expand-row--head">
          <span>Periodo</span>
          <span>Margen</span>
        </div>
        <div
          v-for="h in marginHistory || []"
          :key="h.periodKey"
          class="margin-card__expand-row"
          :class="h.noiMarginPercent === null ? '' : (h.noiMarginPercent >= historyAvg ? 'margin-card__expand-row--above' : 'margin-card__expand-row--below')"
        >
          <span>{{ h.label }}</span>
          <span class="margin-card__expand-amount">{{ formatPercent(h.noiMarginPercent) }}</span>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="margin-card__compare-row">
        <div class="margin-card__compare-col">
          <span class="margin-card__period-label">{{ periodALabel }}</span>
          <span class="margin-card__value margin-card__value--compact" :style="{ color: semanticColor }">{{ formatPercent(period?.noiMarginPercent) }}</span>
        </div>
        <div class="margin-card__compare-divider"></div>
        <div class="margin-card__compare-col">
          <span class="margin-card__period-label margin-card__period-label--muted">{{ periodBLabel }}</span>
          <span class="margin-card__value margin-card__value--compact margin-card__value--muted">{{ formatPercent(comparisonSnapshot?.noiMarginPercent) }}</span>
        </div>
      </div>
      <div class="margin-card__diff-row">
        <span class="margin-card__diff" :class="comparePoints >= 0 ? 'margin-card__sub--good' : 'margin-card__sub--bad'">
          {{ comparePoints >= 0 ? '▲' : '▼' }} {{ Math.abs(comparePoints).toFixed(1) }} pts
        </span>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  period: { type: Object, default: null },
  previousPeriodLabel: { type: String, default: '' },
  marginTrendPoints: { type: Number, default: 0 },
  twelveMonthAvgMargin: { type: Number, default: null },
  marginHistory: { type: Array, default: () => [] },
  comparisonMode: { type: Boolean, default: false },
  comparisonSnapshot: { type: Object, default: null },
  periodALabel: { type: String, default: '' },
  periodBLabel: { type: String, default: '' },
})

const expanded = ref(false)

const semanticColor = computed(() => {
  const pct = props.period?.noiMarginPercent
  if (pct === null || pct === undefined) return '#94a3b8'
  if (pct > 75) return '#10b981'
  if (pct >= 60) return '#f0a500'
  return '#e84545'
})

const trendPoints = computed(() => props.marginTrendPoints ?? 0)
const comparePoints = computed(() => (props.period?.noiMarginPercent ?? 0) - (props.comparisonSnapshot?.noiMarginPercent ?? 0))

const historyAvg = computed(() => {
  const values = (props.marginHistory || []).map((h) => h.noiMarginPercent).filter((v) => v !== null && v !== undefined)
  if (!values.length) return 0
  return values.reduce((a, b) => a + b, 0) / values.length
})

function formatPercent(value) {
  if (value === null || value === undefined) return '—'
  return `${value.toFixed(1)}%`
}
</script>

<style scoped>
.margin-card {
  position: relative;
  background: var(--bg-surface-1);
  border: 1px solid var(--border-active);
  border-radius: 8px;
  padding: 0.6rem 1rem 0.55rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.margin-card__top-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
}

.margin-card__header {
  margin-bottom: 0.3rem;
}

.margin-card__label {
  display: block;
  font-family: var(--font-body);
  font-weight: 500;
  font-size: 0.72rem;
  color: #64748b;
}

.margin-card__sublabel {
  display: block;
  font-family: var(--font-body);
  font-size: 0.65rem;
  color: #94a3b8;
}

.margin-card__body {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  width: 100%;
}

.margin-card__value {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 1.6rem;
  flex-shrink: 0;
}

.margin-card__value--compact {
  font-size: 1.1rem;
  margin-bottom: 0;
}

.margin-card__value--muted {
  color: #94a3b8;
}

.margin-card__sub-col {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.1rem;
  text-align: right;
  min-width: 0;
}

.margin-card__sub {
  font-family: var(--font-body);
  font-size: 0.68rem;
  color: #64748b;
  line-height: 1.35;
}

.margin-card__sub--good {
  color: #10b981;
}

.margin-card__sub--bad {
  color: #e84545;
}

.margin-card__sub--neutral {
  color: #94a3b8;
}

.margin-card__expand {
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px solid #f1f5f9;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.margin-card__expand-row {
  display: flex;
  justify-content: space-between;
  font-family: var(--font-body);
  font-size: 0.75rem;
  color: var(--text-content);
  padding: 0.1rem 0.3rem;
  border-radius: 4px;
}

.margin-card__expand-row--head {
  color: #94a3b8;
  font-size: 0.65rem;
  text-transform: uppercase;
}

.margin-card__expand-row--above {
  background: #ecfdf5;
  color: #047857;
}

.margin-card__expand-row--below {
  background: #fef2f2;
  color: #b91c1c;
}

.margin-card__expand-amount {
  font-family: var(--font-mono);
}

.margin-card__compare-row {
  display: flex;
  align-items: stretch;
  gap: 0.6rem;
}

.margin-card__compare-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.margin-card__compare-divider {
  width: 1px;
  background: #e2e8f0;
  align-self: stretch;
}

.margin-card__period-label {
  font-size: 0.65rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.margin-card__period-label--muted {
  color: #94a3b8;
}

.margin-card__diff-row {
  display: flex;
  justify-content: center;
  margin-top: 0.4rem;
  padding-top: 0.3rem;
  border-top: 1px solid #f1f5f9;
}

.margin-card__diff {
  font-family: var(--font-mono);
  font-size: 0.7rem;
  font-weight: 600;
}
</style>